import { hasInjectionContext, inject, type InjectionKey } from 'vue'
import { DefaultApolloClient, useApolloClient } from '@vue/apollo-composable'
import type { ApolloClient } from '@apollo/client/core'

export type { ApolloClient } from '@apollo/client/core'

/**
 * Apollo port — host-provided Apollo client (the singleton from
 * `src/apollo/index.ts` today; relocated to `src/shared/api/apolloClient.ts`
 * in Phase 6).
 *
 * `useApollo()` resolves from `APOLLO_PORT` first, then the existing
 * `@vue/apollo-composable` `DefaultApolloClient` provide, then the plugin's
 * own `useApolloClient()` (which reads from `provideApolloClient(state)`,
 * reachable from anywhere — including outside setup). The chained lookup
 * keeps callers alive during the migration window where either provide may
 * be the one wired up.
 */
export const APOLLO_PORT: InjectionKey<ApolloClient<unknown>> = Symbol('ApolloPort')

export const useApollo = (): ApolloClient<unknown> => {
  if (hasInjectionContext()) {
    const fromPort = inject(APOLLO_PORT, null)
    if (fromPort) return fromPort
    const fromDefault = inject(DefaultApolloClient, null)
    if (fromDefault) return fromDefault as ApolloClient<unknown>
  }
  const plugin = useApolloClient()
  if (plugin) return plugin.client as unknown as ApolloClient<unknown>
  throw new Error('ApolloPort not provided and DefaultApolloClient is missing')
}
